<template>
    <div class="find-offers">
        <checkbox-input
            v-if="showMapToggle"
            class="find-offers__map-switch"
            :label="$t('pages.offer.find_offers.view_map')"
            :value="isMapOpen"
            type="switch"
            labelPosition="left"
            @change="toggleMap"
        ></checkbox-input>
        <div class="find-offers__results">
            <div v-show="showResults" class="find-offers__results--items">
                <div v-if="showOffers">
                    <common-grid-items v-if="filter">
                        <offer-card-component
                            v-for="offer in offers"
                            :key="offer.id"
                            :offer="offer"
                            @location-click="onLocationClicked"
                        ></offer-card-component>
                    </common-grid-items>
                    <common-grid-items v-if="companyOffers">
                        <offer-card-component
                            v-for="offer in companyOffers"
                            :key="offer.id"
                            :offer="offer"
                            @location-click="onLocationClicked"
                        ></offer-card-component>
                    </common-grid-items>

                    <common-grid-items v-if="arrayUserOffers">
                        <offer-card-component
                            v-for="offer in arrayUserOffers"
                            :key="offer.id"
                            :offer="offer"
                            @location-click="onLocationClicked"
                        ></offer-card-component>
                    </common-grid-items>
                    <div v-if="arrayUserCompanyOffers">
                        <div
                            v-for="userCompanyOffers in arrayUserCompanyOffers"
                            :key="userCompanyOffers"
                            class="find-offers__results--user-company"
                        >
                            <h2 class="find-offers__results--user-name">{{ userCompanyOffers.company.name }}</h2>
                            <common-grid-items>
                                <offer-card-component
                                    v-for="offer in userCompanyOffers.offers"
                                    :key="offer"
                                    :offer="offer"
                                    @location-click="onLocationClicked"
                                ></offer-card-component>
                            </common-grid-items>
                        </div>
                    </div>
                    <common-buttons v-show="offers.length < totalCount" class="find-offers__results--showmore">
                        <common-button
                            type="primary"
                            :isProcessing="isLoading"
                            :label="$t('common.microcopy.show_more')"
                            @click="getOffers(getFilter(), true)"
                        ></common-button>
                    </common-buttons>
                </div>
                <div v-else-if="!isLoading" class="find-offers__results--noresult">
                    <span>{{ $t('pages.offer.find_offers.no_results') }}</span>
                </div>
            </div>
            <transition name="slide-fade">
                <map-component
                    v-show="showMapComponent"
                    ref="mapComponent"
                    :showCurrentLocation="true"
                    class="find-offers__results--map"
                    :markers="markers"
                    :mapCenter="mapCenter"
                    @map-position-changed="onMapPositionChanged"
                ></map-component>
            </transition>
        </div>
    </div>
</template>

<script>
    import Store from '@/store'
    import MapComponent from 'components/page-elements/locations/map/MapComponent'
    import OfferCardComponent from 'components/page-elements/offer/OfferCardComponent'
    import CheckboxInput from 'components/form-inputs/CheckboxInput.vue'
    import OfferAPI from 'api/OfferAPI'
    import WindowResizeMixin from 'mixins/WindowResizeMixin'

    export default {
        mixins: [WindowResizeMixin],

        components: { MapComponent, CheckboxInput, OfferCardComponent },

        props: {
            filter: { type: Object, default: undefined },
            companyOffers: { type: Object, default: undefined },
            userOffers: { type: Object, default: undefined },
            userLocation: { type: Object, default: undefined },
            isCompanyPublicPage: { type: Boolean, default: false },
            showMapToggle: { type: Boolean, default: true },
            showMap: { type: Boolean, default: true },
        },

        data: () => ({
            offers: [],
            totalCount: 0,
            isMapOpen: true,
            arrayUserOffers: [],
            arrayUserCompanyOffers: [],
            mapCenter: undefined,
            markers: [],
            isLoading: false,
            limit: 30,
            offset: 0,
            userOffersMarkers: [],
            userCompanyOffersMarkers: [],
            arrayUserCompanyOffersMarkers: [],
            currentCoords: undefined,
            locationIds: [],
            distance: null,
            longitude: null,
            latitude: null,
            isFirstMapLoad: true,
            isMapPositionChangeLoading: false,
        }),

        computed: {
            showMapComponent() {
                return (this.showMapToggle && this.isMapOpen) || (!this.showMapToggle && this.showMap)
            },
            showResults() {
                return !(this.isSmartphoneLarge && this.isMapOpen)
            },

            showOffers() {
                return this.offers?.length || this.totalCount || this.companyOffers?.length || this.userOffers?.length
            },
        },

        methods: {
            toggleMap() {
                this.isMapOpen = !this.isMapOpen
            },

            fetchOffers(filter, shouldAppend = false) {
                this.isLoading = true
                if (!shouldAppend) {
                    Store.dispatch('configuration/setIsLoading', true)
                }
                const request = {
                    ...filter,
                    take: this.limit,
                    skip: this.offset,
                    longitude: this.longitude,
                    latitude: this.latitude,
                }

                request.distance = this.distance
                return OfferAPI.findOffers(request)
                    .then((res) => {
                        this.updateOffers(res, shouldAppend)
                    })
                    .catch(() => {
                        this.isLoading = true
                        Store.dispatch('configuration/setIsLoading', false)
                        this.$toast.error(this.$t('common.errors.common_error'))
                    })
            },

            updateOffers(response, shouldAppend) {
                const { page, total } = response
                this.offers = shouldAppend ? [...this.offers, ...page] : page
                const newLocationIds = new Set(page.map((item) => item.location.id))
                const existingLocationIds = new Set(this.locationIds)
                this.locationIds = [...new Set([...this.locationIds, ...newLocationIds])]
                const uniqueDifference = [...newLocationIds].filter((id) => !existingLocationIds.has(id))
                this.totalCount = total
                this.offset = this.offers.length
                this.isLoading = false
                Store.dispatch('configuration/setIsLoading', false)
                if (uniqueDifference?.length) {
                    this.getOfferMapResults(this.getFilter(), uniqueDifference)
                }
            },

            getFilter() {
                if (!this.filter) return
                const filter = Object.assign(this.filter)
                if (filter.fromPrice) {
                    filter.fromPrice = Number(filter.fromPrice)
                }
                if (filter.toPrice) {
                    filter.toPrice = Number(filter.toPrice)
                }
                if (filter.serviceId) {
                    filter.serviceId = Number(filter.serviceId)
                } else if (filter.serviceGroupId) {
                    filter.serviceGroupId = Number(filter.serviceGroupId)
                }
                if (filter.cityId) {
                    filter.cityId = Number(filter.cityId)
                }

                if (filter.includeDescription === 'true') {
                    filter.includeDescription = true
                }
                return filter
            },

            onLocationClicked(event) {
                this.setMapCenter(event)
                this.$refs.mapComponent.zoom = 17
                this.$refs.mapComponent.isOpenPopupActive = true
                setTimeout(() => {
                    this.$refs.mapComponent.$refs[`marker${event.locationId}`][0].leafletObject.openPopup()
                    this.$refs.mapComponent.isOpenPopupActive = false
                }, 1000)
            },

            removeCityAndCountryParams() {
                const query = { ...this.$route.query }
                delete query.cityId
                delete query.countryId
                this.$router.replace({ query })
            },

            onMapPositionChanged(payload) {
                if (this.$auth.isSubdomainActive()) {
                    return
                }

                this.removeCityAndCountryParams()
                this.isLoading = true
                const filter = this.getFilter()

                this.isMapPositionChangeLoading = !!(filter.countryId || filter.cityId)

                this.distance = payload.distance
                this.longitude = payload.center.lng
                this.latitude = payload.center.lat
                if (this.longitude < -179) {
                    this.longitude = -179
                } else if (this.longitude > 179) {
                    this.longitude = 179
                }
                if (this.latitude < -89) {
                    this.latitude = -89
                } else if (this.latitude > 89) {
                    this.latitude = 89
                }
                this.locationIds = []
                this.limit = 30
                this.offset = 0
                delete filter.countryId
                delete filter.cityId

                return this.fetchOffers(filter, false)
            },

            getOffers(filter, shouldAppend) {
                return this.fetchOffers(filter, shouldAppend)
            },

            getOfferMapResults(filter, uniqueDifference) {
                const request = {
                    ...filter,
                    locationIds: uniqueDifference,
                    orderBy: undefined,
                    wayDefinition: undefined,
                }

                return OfferAPI.findOffersForMap(request).then((res) => {
                    this.createMapMarkers(res, uniqueDifference[0])
                })
            },

            createMapMarkers(data, firstLocationId) {
                const newMarkers = this.mapData(data).filter(
                    (newMarker) =>
                        !this.markers.some(
                            (existingMarker) =>
                                existingMarker.coordinates?.lat === newMarker?.coordinates?.lat &&
                                existingMarker.coordinates?.lng === newMarker?.coordinates?.lng,
                        ),
                )
                this.markers = [...this.markers, ...newMarkers]

                if (this.isFirstMapLoad) {
                    const marker = this.markers.find((item) => item.id === firstLocationId)
                    this.setMapCenter(marker.coordinates, false)
                    this.isFirstMapLoad = false
                }
            },

            setMapCenter(object, openTheMap = true) {
                if (!object) {
                    return
                }
                if (!this.isMapOpen && openTheMap) {
                    this.isMapOpen = true
                }

                setTimeout(() => {
                    this.mapCenter = { lat: object?.lat, lng: object?.lng }
                    if (this.$refs.mapComponent) {
                        this.$refs.mapComponent.center = this.mapCenter
                    }
                }, 100)
            },

            zoomCity(cityId) {
                const city = Store.getters['data/getCountries'][1]?.cities?.find((city) => city.id === cityId)
                if (city) {
                    this.setMapCenter({ lat: city.latitude, lng: city.longitude }, !this.isSmartphoneLarge)
                }
            },

            fetchCompanyMapData() {
                if (this.$auth.isSubdomainActive() || this.isCompanyPublicPage) {
                    const uniqueLocationIds = new Set()
                    const filteredData = []
                    this.companyOffers.forEach((item) => {
                        const locationId = item.location.id
                        if (!uniqueLocationIds.has(locationId)) {
                            uniqueLocationIds.add(locationId)
                            filteredData.push(item)
                        }
                    })
                    this.markers = this.mapData(filteredData)
                    this.setMapCenter(this.markers[0]?.coordinates)
                } else {
                    this.markers = this.mapData(this.companyOffers)
                }
            },

            fetchUserMapData() {
                this.userOffersMarkers = this.mapData(this.arrayUserOffers)
                this.userCompanyOffersMarkers = this.mapData(this.arrayUserCompanyOffersMarkers)
                const newSet = new Set()
                const filteredData = []

                this.userOffersMarkers.filter((item) => {
                    const key = `${item.coordinates.lat},${item.coordinates.lng}`
                    if (!newSet.has(key)) {
                        newSet.add(key)
                        filteredData.push(item)
                    }
                })
                this.markers = [...filteredData, ...this.userCompanyOffersMarkers]
            },
            fetchUserOffersData() {
                if (this.userOffers.length) {
                    this.arrayUserOffers = this.userOffers
                }
                if (this.userOffers.companyOffers) {
                    this.arrayUserCompanyOffers = this.userOffers.companyOffers

                    let tmpArray = []

                    this.arrayUserCompanyOffers.map((value) => {
                        value.offers.map((off) => {
                            tmpArray.push(off)
                        })
                    })

                    this.arrayUserCompanyOffersMarkers = tmpArray
                }
            },
            mapData(data) {
                return data.map((el) => ({
                    offerCount: el.activeOfferCount,
                    averageRating: el.averageRating,
                    numberOfRatings: el.numberOfRatings,
                    isCompanyOffer: !!el.location.companyServiceProvider,
                    providerData: el.location.companyServiceProvider || el.location.userServiceProvider,
                    coordinates: { lat: el.location.latitude, lng: el.location.longitude },
                    id: el.location.id,
                }))
            },
        },

        watch: {
            filter() {
                if (this.isMapPositionChangeLoading) {
                    this.isMapPositionChangeLoading = false
                    return
                }
                this.isFirstMapLoad = true
                this.longitude = this.userLocation?.longitude
                this.latitude = this.userLocation?.latitude
                this.distance = null
                this.offset = 0
                this.locationIds = []
                this.markers = []
                const filter = this.getFilter()
                this.getOffers(filter)
                if (filter.cityId) {
                    this.zoomCity(filter.cityId)
                }
            },
        },

        mounted() {
            if (this.companyOffers) {
                this.fetchCompanyMapData()
            }

            if (this.userOffers) {
                this.fetchUserOffersData()
                this.fetchUserMapData()
            }

            if (this.isSmartphoneLarge) {
                this.isMapOpen = false
            }
        },
    }
</script>

<style lang="scss" scoped>
    @import 'styles/components/offers/_offer-results.scss';
</style>
